import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { Zeppelin } from 'ts-core';
import { useZeppelinsV2 } from '../hooks/useZeppelinsV2';
import { useMapBox } from './BalloonEventsMapBox';
import { Dialog } from 'react-ui';
import { useBalloonEventsData } from '../contexts/EventData';
import { trackMixpanel } from '../utils/mixpanel';
import { useBalloonEventsMetadata, useQuery } from 'react-ui';
import { useZeppelinPosition } from '../contexts/ZeppelinPosition';

const ZEPPELIN_BETWEEN_MS = parseInt(import.meta.env.VITE_ZEPPELIN_BETWEEN_MS ?? '20000', 10);
const ZEPPELIN_INITIAL_MS  = parseInt(import.meta.env.VITE_ZEPPELIN_INITIAL_MS  ?? '5000',  10);

function getRandomBottom() {
  return `${Math.random() * 20 + 40}%`;
}

function getZeppelinBodyImageUrl(zeppelin: Zeppelin) {
  return zeppelin.generatedImageUrl || zeppelin.imageUrl || zeppelin.logoUrl || null;
}

function getZeppelinDialogImageUrl(zeppelin: Zeppelin) {
  return zeppelin.imageUrl || zeppelin.generatedImageUrl || zeppelin.logoUrl || null;
}

function getZeppelinSize(zeppelin: Zeppelin) {
  switch (zeppelin.size) {
    case 'small':
      return { width: 140, height: 76 };
    case 'large':
      return { width: 260, height: 140 };
    case 'medium':
    default:
      return { width: 200, height: 108 };
  }
}

function getDirectionClass(flightDirection: number) {
  return Math.sin((flightDirection * Math.PI) / 180) >= 0 ? 'animate-move-right' : 'animate-move-left';
}

function getTrajectoryClass(flightTrajectory: Zeppelin['flightTrajectory']) {
  switch (flightTrajectory) {
    case 'wave':
      return 'animate-zeppelin-wave';
    case 'arch_up':
      return 'animate-zeppelin-arch-up';
    case 'arch_down':
      return 'animate-zeppelin-arch-down';
    case 'straight':
    default:
      return '';
  }
}

function getYouTubeEmbedUrl(url: string | null) {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu.be')) {
      const id = parsed.pathname.split('/').filter(Boolean)[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }

    if (parsed.hostname.includes('youtube.com')) {
      const id = parsed.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
  } catch {
    return null;
  }

  return null;
}

function getCtaLabel(zeppelin: Zeppelin) {
  switch (zeppelin.link.type) {
    case 'location':
      if (zeppelin.startLat == null || zeppelin.startLng == null) return null;
      return zeppelin.ctaLabel?.trim() || 'Show location';
    case 'balloon':
      return zeppelin.ctaLabel?.trim() || 'Open event';
    case 'category':
    case 'tag':
      return zeppelin.ctaLabel?.trim() || 'Apply filter';
    case 'standalone':
      if (zeppelin.deepLinkUrl || zeppelin.externalUrl) {
        return zeppelin.ctaLabel?.trim() || 'Learn more';
      }
      return zeppelin.ctaLabel?.trim() || null;
    default:
      return zeppelin.ctaLabel?.trim() || null;
  }
}

function ZeppelinVisual({ zeppelin }: { zeppelin: Zeppelin }) {
  const bodyImageUrl = getZeppelinBodyImageUrl(zeppelin);
  const { width, height } = getZeppelinSize(zeppelin);
  const opacity = Math.max(0, Math.min(1, zeppelin.opacity / 100));
  const background = `linear-gradient(135deg, ${zeppelin.colorPrimary || '#BF2088'}, ${zeppelin.colorSecondary || zeppelin.colorPrimary || '#F4E3F7'})`;
  const borderRadius = zeppelin.template === 'banner'
    ? '1rem'
    : zeppelin.template === 'round'
      ? '9999px'
      : '9999px';

  if (bodyImageUrl) {
    return (
      <img
        className="object-contain drop-shadow-xl"
        src={bodyImageUrl}
        alt={zeppelin.title}
        style={{ width, height, opacity }}
      />
    );
  }

  return (
    <div
      className="relative flex items-center justify-center text-white shadow-xl overflow-hidden px-4"
      style={{ width, height, opacity, background, borderRadius }}
    >
      {zeppelin.logoUrl ? (
        <img
          src={zeppelin.logoUrl}
          alt={zeppelin.title}
          className="absolute left-3 top-1/2 h-10 w-10 -translate-y-1/2 rounded-full bg-white/80 object-contain p-1"
        />
      ) : null}
      <span className={`block max-w-full truncate text-center font-semibold ${zeppelin.logoUrl ? 'pl-12' : ''}`}>
        {zeppelin.title}
      </span>
    </div>
  );
}

export function ZeppelinContainerV2() {
  const mapBoxInfo = useMapBox();
  const map = mapBoxInfo?.map ?? null;
  const { addQuery, has, queries } = useQuery();
  const activeTagSlugs = useMemo(
    () => queries.flatMap((q) => q.tags ?? []),
    [queries]
  );
  const { zeppelins } = useZeppelinsV2(map, activeTagSlugs);
  const { ensureLoad, selectEvent, setOpenedEvent } = useBalloonEventsData();
  const { metadata } = useBalloonEventsMetadata();
  const { updatePosition } = useZeppelinPosition();
  const navigate = useNavigate();

  const [zeppelinIndex, setZeppelinIndex] = useState(0);
  const [bottom, setBottom] = useState(getRandomBottom());
  const [selectedZeppelinId, setSelectedZeppelinId] = useState<string | null>(null);
  const [isResting, setIsResting] = useState(true);

  const zeppelinRef = useRef<HTMLButtonElement>(null);
  const restTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialTimerStartedRef = useRef(false);

  const selectedZeppelin = useMemo(
    () => zeppelins.find((zeppelin) => zeppelin.id === selectedZeppelinId) ?? null,
    [selectedZeppelinId, zeppelins]
  );

  const currentZeppelin = useMemo(() => {
    if (zeppelins.length === 0) return null;
    return zeppelins[((zeppelinIndex % zeppelins.length) + zeppelins.length) % zeppelins.length] ?? null;
  }, [zeppelinIndex, zeppelins]);

  useEffect(() => {
    if (!currentZeppelin) {
      updatePosition(null);
      return;
    }

    let animationFrameId: number;
    let isRunning = true;

    const broadcastPosition = () => {
      if (!isRunning) return;
      
      if (zeppelinRef.current) {
        updatePosition(zeppelinRef.current.getBoundingClientRect());
      }
      animationFrameId = requestAnimationFrame(broadcastPosition);
    };

    // Small delay to ensure button is rendered
    const startId = requestAnimationFrame(broadcastPosition);

    return () => {
      isRunning = false;
      cancelAnimationFrame(startId);
      cancelAnimationFrame(animationFrameId);
      updatePosition(null);
    };
  }, [updatePosition, currentZeppelin]);

  useEffect(() => {
    return () => {
      if (restTimeoutRef.current) clearTimeout(restTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (zeppelins.length === 0) {
      setSelectedZeppelinId(null);
      return;
    }

    if (zeppelinIndex >= zeppelins.length) {
      setZeppelinIndex(0);
    }
  }, [zeppelinIndex, zeppelins]);

  useEffect(() => {
    if (currentZeppelin) {
      setBottom(getRandomBottom());
    }
  }, [currentZeppelin?.id]);

  useEffect(() => {
    if (selectedZeppelinId && !selectedZeppelin) {
      setSelectedZeppelinId(null);
    }
  }, [selectedZeppelin, selectedZeppelinId]);

  const openDialog = useCallback((zeppelin: Zeppelin) => {
    trackMixpanel('Zeppelin', 'Click', {
      zeppelinId: zeppelin.id,
      zeppelinTitle: zeppelin.title,
      linkType: zeppelin.link.type,
    });

    // Partner zeppelins open the partner profile experience (scoped map when
    // the partner has events, otherwise just the detail pane over the generic
    // list) instead of a dialog.
    if (zeppelin.link.type === 'partner' && zeppelin.link.partnerCode) {
      navigate(`/partner/${zeppelin.link.partnerCode}`);
      return;
    }

    setSelectedZeppelinId(zeppelin.id);
  }, [navigate]);

  const closeDialog = useCallback(() => {
    setSelectedZeppelinId(null);
  }, []);

  useEffect(() => {
    if (zeppelins.length === 0) {
      initialTimerStartedRef.current = false;
      setIsResting(true);
      return;
    }
    if (initialTimerStartedRef.current) return;
    initialTimerStartedRef.current = true;
    restTimeoutRef.current = setTimeout(() => setIsResting(false), ZEPPELIN_INITIAL_MS);
    return () => {
      if (restTimeoutRef.current) clearTimeout(restTimeoutRef.current);
    };
  }, [zeppelins.length]);

  const handleAnimationIteration = useCallback((event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.animationName !== 'move-right' && event.animationName !== 'move-left') return;
    setIsResting(true);
    setZeppelinIndex((current) => current + 1);
    restTimeoutRef.current = setTimeout(() => setIsResting(false), ZEPPELIN_BETWEEN_MS);
  }, []);

  const handleDialogCta = useCallback(async () => {
    if (!selectedZeppelin) return;

    trackMixpanel('Zeppelin', 'CTA', {
      zeppelinId: selectedZeppelin.id,
      zeppelinTitle: selectedZeppelin.title,
      linkType: selectedZeppelin.link.type,
    });

    if (selectedZeppelin.link.type === 'location') {
      if (map && selectedZeppelin.startLng != null && selectedZeppelin.startLat != null) {
        map.flyTo({
          center: [selectedZeppelin.startLng, selectedZeppelin.startLat],
          zoom: 15,
          duration: 1500,
        });
      }
      closeDialog();
      return;
    }

    if (selectedZeppelin.link.type === 'balloon') {
      const identifier = selectedZeppelin.link.slug || selectedZeppelin.link.id;
      if (!identifier) return;

      try {
        const event = await ensureLoad(identifier);
        if (!event) return;

        if (map && event.location?.lng != null && event.location?.lat != null) {
          map.flyTo({
            center: [event.location.lng, event.location.lat],
            zoom: 15,
            duration: 1500,
          });
        }

        selectEvent(event.id);
        setOpenedEvent(event);
        closeDialog();
      } catch (error) {
        console.error('[ZeppelinContainerV2] Failed to load event for balloon CTA:', error);
      }
      return;
    }

    if (selectedZeppelin.link.type === 'category') {
      if (!metadata) return;

      const category = selectedZeppelin.link.slug
        ? metadata.getCategoryBySlug(selectedZeppelin.link.slug)
        : null;

      if (!category || has(category.id)) {
        closeDialog();
        return;
      }

      addQuery({ id: category.id, categories: [category.id] });
      closeDialog();
      return;
    }

    if (selectedZeppelin.link.type === 'tag') {
      if (!metadata || !selectedZeppelin.link.slug) return;

      const tag = metadata.tags.find((item) => item.slug === selectedZeppelin.link.slug);
      if (!tag) return;

      const queryId = `tag:${tag.slug}`;
      if (!has(queryId)) {
        addQuery({ id: queryId, tags: [tag.slug] });
      }
      closeDialog();
      return;
    }

    // Fallback: deep link or external URL (used by 'standalone' and any unhandled types)
    if (selectedZeppelin.deepLinkUrl) {
      window.dispatchEvent(new CustomEvent('balloonevents:deeplink', { detail: selectedZeppelin.deepLinkUrl }));
      closeDialog();
      return;
    }

    if (selectedZeppelin.externalUrl) {
      window.open(selectedZeppelin.externalUrl, '_blank', 'noopener,noreferrer');
      closeDialog();
      return;
    }

    // No URL available — just close the dialog
    closeDialog();
  }, [addQuery, closeDialog, ensureLoad, has, map, metadata, selectEvent, selectedZeppelin, setOpenedEvent]);

  const ctaLabel = selectedZeppelin ? getCtaLabel(selectedZeppelin) : null;
  const dialogImageUrl = selectedZeppelin ? getZeppelinDialogImageUrl(selectedZeppelin) : null;
  const videoEmbedUrl = getYouTubeEmbedUrl(selectedZeppelin?.videoYoutubeUrl ?? null);
  const directionClass = currentZeppelin ? getDirectionClass(currentZeppelin.flightDirection) : '';
  const trajectoryClass = currentZeppelin ? getTrajectoryClass(currentZeppelin.flightTrajectory) : '';
  const verticalHeadroom = 64;

  if (!currentZeppelin) return null;

  return (
    <>
      {!isResting && (
        <div
          style={{ bottom: `calc(${bottom} - ${verticalHeadroom}px)` }}
          className="absolute w-full pointer-events-none overflow-hidden pt-16"
        >
          <div
            onAnimationIteration={handleAnimationIteration}
            className={`${directionClass} flex`}
          >
            <div className={trajectoryClass}>
              <button
                ref={zeppelinRef}
                type="button"
                className="pointer-events-auto animate-float bg-transparent p-0 border-0"
                onClick={() => openDialog(currentZeppelin)}
                aria-haspopup="dialog"
                aria-label={currentZeppelin.title}
              >
                <ZeppelinVisual zeppelin={currentZeppelin} />
              </button>
            </div>
          </div>
        </div>
      )}

      {import.meta.env.DEV ? (
        <div className="absolute bottom-12 right-2 z-50 pointer-events-none">
          <div className="bg-black/60 text-white text-xs px-2 py-1 rounded-full font-mono">
            zeppelins v2: {zeppelins.length}
          </div>
        </div>
      ) : null}

      {createPortal(
        // `.be-embedded-app` wrapper carries the scoped Tailwind CSS into
        // portal content rendered at document.body (outside the embed root).
        <div className="be-embedded-app">
          <Dialog
            open={selectedZeppelin != null}
            onClose={closeDialog}
            title={selectedZeppelin?.title}
            maxWidth="640px"
          >
            {selectedZeppelin ? (
              <div className="flex flex-col gap-4">
                {videoEmbedUrl ? (
                  <div className="overflow-hidden rounded-2xl bg-black">
                    <iframe
                      className="aspect-video w-full"
                      src={videoEmbedUrl}
                      title={selectedZeppelin.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : null}

                {!videoEmbedUrl && selectedZeppelin.videoUrl ? (
                  <video
                    className="w-full rounded-2xl bg-black"
                    controls
                    src={selectedZeppelin.videoUrl}
                  />
                ) : null}

                {!videoEmbedUrl && !selectedZeppelin.videoUrl && dialogImageUrl ? (
                  <img
                    src={dialogImageUrl}
                    alt={selectedZeppelin.title}
                    className="max-h-[320px] w-full rounded-2xl object-cover"
                  />
                ) : null}

                {selectedZeppelin.descriptionHtml ? (
                  <div
                    className="prose prose-sm max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: selectedZeppelin.descriptionHtml }}
                  />
                ) : selectedZeppelin.body ? (
                  <div className="flex flex-col gap-3">
                    <p className="whitespace-pre-line text-sm leading-6 text-gray-700">
                      {selectedZeppelin.body}
                    </p>
                  </div>
                ) : null}

                {ctaLabel ? (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="rounded-full bg-balloon px-5 py-2 text-sm font-medium text-white transition hover:opacity-90"
                      onClick={() => {
                        void handleDialogCta();
                      }}
                    >
                      {ctaLabel}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </Dialog>
        </div>,
        document.body
      )}
    </>
  );
}
